import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import { Link } from "react-router-dom";
import {
  Datagrid,
  DateInput,
  FilterButton,
  FunctionField,
  List,
  ReferenceInput,
  SearchInput,
  SelectInput,
  Show,
  TextField,
  TopToolbar,
  useRecordContext,
  usePermissions,
  type ListProps,
  type ShowProps,
} from "react-admin";
import CustomEmpty from "@/components/custom-empty";
import { DisplayDateField } from "@/components/DisplayDateField";
import { hasPermission } from "@/utils/permission-utils";
import { formatRupees } from "../shared/fee-format";
import { MADRASA_FEES_CREATE } from "../shared/madrasa-permissions";
import { RecordTitle } from "../shared/RecordTitle";
import { FeeReceiptSheet, type ReceiptRecord } from "./FeeReceiptSheet";
import { VoidReceiptButton } from "./VoidReceiptButton";

const MODE_CHOICES = [
  { id: "CASH", name: "Cash" },
  { id: "BANK_TRANSFER", name: "Bank transfer" },
  { id: "CHEQUE", name: "Cheque" },
  { id: "UPI", name: "UPI" },
];

const listFilters = [
  <SearchInput key="q" source="q" placeholder="Receipt no, student, ITS, admission no" alwaysOn />,
  <ReferenceInput key="academicYearId" source="academicYearId" reference="academicYears" alwaysOn>
    <SelectInput optionText="name" label="Academic year" emptyText="All years" />
  </ReferenceInput>,
  <SelectInput
    key="paymentMode"
    source="paymentMode"
    label="Paid by"
    choices={MODE_CHOICES}
    alwaysOn
  />,
  <SelectInput
    key="voided"
    source="voided"
    label="Status"
    choices={[
      { id: "false", name: "Valid" },
      { id: "true", name: "Voided" },
    ]}
    alwaysOn
  />,
  <DateInput key="dateFrom" source="dateFrom" label="From date" />,
  <DateInput key="dateTo" source="dateTo" label="To date" />,
];

const ListActions = () => {
  const { permissions } = usePermissions();
  return (
    <TopToolbar>
      {/* A custom toolbar replaces the default one, so bring back the menu for the date filters. */}
      <FilterButton />
      {hasPermission(permissions, MADRASA_FEES_CREATE) && (
        <Button component={Link} to="/feeCollect" variant="contained" size="small">
          Collect fee
        </Button>
      )}
    </TopToolbar>
  );
};

export function FeeReceiptsList(props: ListProps) {
  return (
    <List
      {...props}
      sort={{ field: "receiptDate", order: "DESC" }}
      perPage={50}
      filters={listFilters}
      actions={<ListActions />}
      empty={
        <CustomEmpty
          title="No receipts yet"
          subtitle="Receipts appear here when you collect a fee."
          action={
            <Button component={Link} to="/feeCollect" variant="contained">
              Collect fee
            </Button>
          }
        />
      }
    >
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <TextField source="receiptNo" label="Receipt no" />
        <DisplayDateField source="receiptDate" label="Date" />
        <TextField source="studentName" label="Student" />
        <TextField source="admissionNo" label="Adm. no" />
        <TextField source="academicYearName" label="Year" />
        <FunctionField
          label="Amount"
          textAlign="right"
          render={(r: { totalAmount: number }) => formatRupees(r.totalAmount)}
        />
        <FunctionField
          label="Paid by"
          render={(r: { paymentMode: string; paymentReference: string | null }) =>
            `${MODE_CHOICES.find((m) => m.id === r.paymentMode)?.name ?? r.paymentMode}${r.paymentReference ? ` · ${r.paymentReference}` : ""}`
          }
        />
        <FunctionField
          label="Status"
          render={(r: { voided: boolean }) =>
            r.voided ? (
              <Chip size="small" color="error" label="Voided" />
            ) : (
              <Chip size="small" color="success" variant="outlined" label="Valid" />
            )
          }
        />
      </Datagrid>
    </List>
  );
}

const ShowActions = () => (
  <TopToolbar>
    <Button onClick={() => window.print()}>Print</Button>
    <VoidReceiptButton />
  </TopToolbar>
);

function ReceiptBody() {
  const record = useRecordContext<ReceiptRecord>();
  // react-admin first shows the row it already has from the list, which only has a count of line
  // items. Wait for the full receipt (with its line items) before drawing the sheet.
  if (!record?.allocations) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  return <FeeReceiptSheet receipt={record} />;
}

export function FeeReceiptsShow(props: ShowProps) {
  return (
    <Show
      {...props}
      actions={<ShowActions />}
      title={<RecordTitle prefix="Receipt" sources={["receiptNo"]} />}
    >
      <ReceiptBody />
    </Show>
  );
}

export default {
  list: FeeReceiptsList,
  show: FeeReceiptsShow,
  icon: ReceiptLongIcon,
  label: "Receipts",
  options: { label: "Receipts" },
  name: "feeReceipts",
};
